/**
 * 酒店结算二维码弹窗组件
 * 从 CrossEvent-Web（Vue3）重构而来，对应原 QRCodeModal.vue
 *
 * 触发方式：
 *   1. 在 HomePage/index.jsx 的 rowMenuHandleClick 中添加 case 'settle_QrCode'
 *      分支，调用本组件的 show(record) 方法
 *   2. 本组件内部按行数据组装 cacheData、codeParams，
 *      调用 ExecuteChain(event_xitem_has_tinyurl) 判断是否已生成，
 *      未生成进入 EditPhoneTable 编辑执行人手机号，
 *      已生成直接展示二维码并提供复制链接/图片/下载/调整有效期等能力
 *
 * 关键 API：
 *   - commonService.allVenueCostTypeInfo  查询酒店费用信息
 *   - commonService.ExecuteChain           生成/查询短链（event_xitem_tinyurl / event_xitem_has_tinyurl）
 *   - commonService.IqueryColumn/IqueryData 执行人列表查询
 *   - commonService.ExecuteChainSingle     单条新增/编辑执行人
 *   - commonService.InvokeMethod           删除执行人（crossevent_payment_user_save · DelUser）
 *   - commonService.XSheetExtract          Excel 解析
 *   - commonService.ExecuteChainImport     批量导入执行人
 *   - commonService.SaveItemExtUser        饿了么手机号保存（仅 type==='imeal'）
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Modal,
  Spin,
  Row,
  Col,
  Button,
  Popover,
  DatePicker,
  message,
} from "antd";
import {
  CopyOutlined,
  QrcodeOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import moment from "moment";
import QRious from "qrious";
import html2canvas from "html2canvas";
import commonService from "@/service/common";
import { getCookie } from "tiny-cookie";
import SmartStorage from "@/utils/storage";
import EditPhoneTable from "./EditPhoneTable";
import PhoneModal from "./PhoneModal";
import BatchPhoneLogsModal from "./BatchPhoneLogsModal";
import QrcodeCard from "./QrcodeCard";
import "./index.less";

const { RangePicker } = DatePicker;

// 默认 qrData 结构
const defaultQrData = {
  title: "酒店结算二维码",
  codeParams: null,
  logoIcon: "iconerweima",
  type: "other",
};

const SettleQrcodeModal = ({ visible, record, onClose }) => {
  // === 调试日志：定位 lilly 分支为何未命中 ===
  console.log("[SettleQrcodeModal] record =", record);
  console.log("[SettleQrcodeModal] record.tenantCode =", record?.tenantCode);
  console.log("[SettleQrcodeModal] record.tuUserId =", record?.tuUserId);
  const [spinning, setSpinning] = useState(false);
  // 是否已生成二维码
  const [isCreatedQrcode, setIsCreatedQrcode] = useState(false);
  // 二维码内容（短链）
  const [initQCode, setInitQCode] = useState("");
  // 二维码有效期 [开始, 结束]
  const [qrCodeInDate, setQrCodeInDate] = useState(null);
  // 执行人手机号列表
  const [phoneList, setPhoneList] = useState([]);
  // 弹窗入参
  const [qrData, setQrData] = useState(defaultQrData);
  // 调整有效期 popover 可见性
  const [timePopoverVisible, setTimePopoverVisible] = useState(false);
  // 临时未确认日期
  const [echoData, setEchoData] = useState(null);
  const [unconfirmedTime, setUnconfirmedTime] = useState(null);
  // 批量编辑手机号弹层
  const [phoneModalViable, setPhoneModalViable] = useState(false);
  // 导入日志弹层
  const [logVisible, setLogVisible] = useState(false);
  // 二维码 canvas ref
  const qrCodeRef = useRef(null);
  // 离屏卡片 ref（用于复制/下载图片）
  const qrcodeCardRef = useRef(null);

  // 会议详情（从 SmartStorage 读取，原 Vue 用 smartStorage.get('itemDetail')）
  const miceInfo = SmartStorage.get("itemDetail") || record || {};

  /**
   * 会议二维码默认有效期：会议开始前一天 ~ 会议结束后一天
   * 多路 fallback：miceInfo → record → 兜底 dtStart+7天
   */
  const computeMeetingQRCodeDateRange = () => {
    // 硬编码：今天开始前一天 ~ 明天结束后一天 → 覆盖今天明天两天
    const start = moment().subtract(1, "day");
    const end = moment().add(1, "day");
    return [start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD")];
  };
  const meetingQRCodeDateRange = computeMeetingQRCodeDateRange();

  /**
   * 弹窗打开时触发
   * 1. 组装 cacheData、codeParams
   * 2. 调用 ExecuteChain(event_xitem_has_tinyurl) 判断是否已生成
   */
  useEffect(() => {
    if (visible && record) {
      initQrcodeData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, record]);

  /**
   * 初始化结算二维码数据
   * 1. 拉取 allVenueCostTypeInfo
   * 2. 组装 cacheData、codeParams
   * 3. 查询是否已生成短链
   */
  const initQrcodeData = async () => {
    try {
      setSpinning(true);
      // 1. 拉取酒店所有费用类型费用信息
      const params = {
        orderId: record.orderId || record._OrderId,
        miceId: record.processId,
        supplierId: record.supplierId || record._SupplierId,
      };
      const res = await commonService.allVenueCostTypeInfo(params);
      if (!res || !res.success) {
        message.error("查询酒店费用信息失败");
        return;
      }

      // 2. 组装 cacheData
      const cacheData = {
        eventData: {
          proposalId: miceInfo.processId || record.processId,
          dtStart: miceInfo.dtStart,
          dtEnd: miceInfo.dtEnd,
          typeDictTxt: miceInfo.extData?.typeDictVal?.key,
          cityDictVal: miceInfo.city,
          provinceDictVal: miceInfo.province,
          tenant_code: miceInfo.tenantCode,
          statusTxt: miceInfo.statusTxt,
          dspName: miceInfo.OwnerName || miceInfo.extData?.OwnerName,
          tenantId: miceInfo.tenantId || record.tenantId,
          orderId: record.orderId || record._OrderId,
        },
        orderDetail: {
          itemId: record.supplierId || record._SupplierId,
          proposalId: miceInfo.processId || record.processId,
          miceId: miceInfo.processId || record.processId,
          ...res.content,
        },
        itemData: { ...record },
        type: "ivenue",
        isH5: true,
      };

      // 3. 组装 codeParams
      //    url 来自 cookie Uris.ipay + '/orderList'
      let ipayUrl = "";
      try {
        const urisCookie = getCookie("Uris");
        if (urisCookie) {
          const uris = JSON.parse(urisCookie);
          ipayUrl = uris?.Uris?.ipay || "";
        }
      } catch (e) {
        console.warn("解析 Uris cookie 失败", e);
      }
      const codeParams = {
        userId:
          record?.tuUserId
            ? record?.tuUserId
            : miceInfo.userId,
        url: ipayUrl ? ipayUrl + "/orderList" : "",
        itemType: 1, // 1 = 酒店
        data: cacheData,
      };

      setQrData({
        title: "酒店结算二维码",
        codeParams,
        logoIcon: "iconerweima",
        type: "other",
      });

      // 4. 查询是否已生成短链
      await qrcodeIsCreated(codeParams);
    } catch (error) {
      console.error("初始化结算二维码数据失败:", error);
      message.error("初始化结算二维码数据失败");
    } finally {
      setSpinning(false);
    }
  };

  /**
   * 查询是否已生成短链
   * - 已生成 → 直接展示原短链（保留有效期）
   * - itemType==101 且未生成 → 默认 [今天, 今天+90天]
   * - 其它未生成 → 默认 [会议开始前一天, 会议结束后一天]
   */
  const qrcodeIsCreated = async (codeParams) => {
    const params = {
      code: "event_xitem_has_tinyurl",
      meta: {
        miceId: codeParams?.data?.eventData?.processId || miceInfo.processId,
        businessId:
          codeParams?.data?.eventData?.orderId ||
          codeParams?.data?.eventData?.itemId,
        itemType: codeParams?.itemType,
      },
    };
    const res = await commonService.ExecuteChain(params);
    if (res && res.success && res.content) {
      const { shortDataInfo } = res.content;
      setQrCodeInDate([
        shortDataInfo.startTime.split("T")[0],
        shortDataInfo.endTime.split("T")[0],
      ]);
      setInitQCode(shortDataInfo.tinyUrl);
      setIsCreatedQrcode(true);
      renderQrcode(shortDataInfo.tinyUrl);
    } else if (codeParams?.itemType === 101) {
      setQrCodeInDate([
        moment().format("YYYY-MM-DD"),
        moment().add(90, "days").format("YYYY-MM-DD"),
      ]);
      setIsCreatedQrcode(false);
    } else {
      setQrCodeInDate(
        meetingQRCodeDateRange ? [...meetingQRCodeDateRange] : null,
      );
      setIsCreatedQrcode(false);
    }
  };

  /**
   * 渲染二维码到 canvas
   */
  const renderQrcode = (text) => {
    setTimeout(() => {
      if (qrCodeRef.current) {
        // eslint-disable-next-line no-new
        new QRious({
          element: qrCodeRef.current,
          value: text,
          size: 100,
          level: "H",
        });
      }
    }, 50);
  };

  /**
   * 生成二维码
   * - itemType !== 101 时校验会议未过期
   */
  const createQRCode = (phones) => {
    setPhoneList(phones);
    createQRCodeContext(undefined, phones);
  };

  /**
   * 生成二维码上下文
   * - 校验日期有效性
   * - 计算 cacheExpiration（按 itemType 分支）
   * - 调用 ExecuteChain(event_xitem_tinyurl)
   */
  const createQRCodeContext = async (
    dateRange = qrCodeInDate,
    phonesOverride,
  ) => {
    // React state 异步：优先用传入的 phonesOverride，fallback 到 state
    const mobiles = phonesOverride !== undefined ? phonesOverride : phoneList;
    if (
      !dateRange ||
      !dateRange[0] ||
      !dateRange[1] ||
      !moment(dateRange[0]).isValid() ||
      !moment(dateRange[1]).isValid() ||
      moment(dateRange[0]).isAfter(moment(dateRange[1]), "day")
    ) {
      message.error("请选择有效的二维码有效期");
      return false;
    }
    // 计算 cacheExpiration
    let cacheExpiration;
    const itemType = qrData?.codeParams?.itemType;
    if (itemType === 101) {
      const timeRange = moment(dateRange[1]).diff(moment(dateRange[0]), "days");
      cacheExpiration = `${timeRange + 1}:0:0:0`;
    } else {
      // 缓存截止到有效期结束日末尾
      const remainingSeconds = Math.ceil(
        moment(dateRange[1]).add(1, "day").startOf("day").diff(moment()) / 1000,
      );
      if (remainingSeconds <= 0) {
        message.error("二维码有效期已过期，请调整有效期");
        return false;
      }
      const days = Math.floor(remainingSeconds / 86400);
      const hours = Math.floor((remainingSeconds % 86400) / 3600);
      const minutes = Math.floor((remainingSeconds % 3600) / 60);
      const seconds = remainingSeconds % 60;
      cacheExpiration = `${days}:${hours}:${minutes}:${seconds}`;
    }

    setSpinning(true);
    const eventDataUp = {
      ...qrData.codeParams.data.eventData,
      miceId:
        qrData?.codeParams?.data?.eventData?.processId || miceInfo.processId,
      businessId:
        qrData?.codeParams?.data?.eventData?.orderId ||
        qrData?.codeParams?.data?.eventData?.itemId,
      itemType: qrData?.codeParams?.itemType,
    };
    const params = {
      code: "event_xitem_tinyurl",
      meta: {
        miceId:
          qrData?.codeParams?.data?.eventData?.processId || miceInfo.processId,
        businessId:
          qrData?.codeParams?.data?.eventData?.orderId ||
          qrData?.codeParams?.data?.eventData?.itemId,
        itemType: qrData?.codeParams?.itemType,
        StartTime: dateRange[0],
        EndTime: dateRange[1],
        url: process.env.REACT_APP_SETTLE_IVENUE_URL,
        swapId: qrData.codeParams.swapId || false,
        generatePolicy: 13,
        assistantFactor: {
          userId:
            record?.tuUserId
              ? record?.tuUserId
              : getCookie("userId"),
          authMobiles: mobiles,
          cacheEntryOptions: {
            absoluteExpirationRelativeToNow: cacheExpiration,
          },
        },
        context: {
          data: {
            ...qrData.codeParams.data,
            eventData: eventDataUp,
            origin: "AssistMeeting",
          },
        },
      },
    };

    // 饿了么分支（仅 type==='imeal'）
    if (
      qrData.codeParams?.data?.type &&
      qrData.codeParams.data.type === "imeal"
    ) {
      const pas = {
        itemId: qrData.codeParams.data.orderDetail.itemId,
        TenantId: miceInfo.tenantId,
        UserPhone: mobiles[0],
      };
      const saveItemRes = await commonService.SaveItemExtUser(pas);
      if (!saveItemRes || !saveItemRes.success) return false;
    }

    try {
      const res = await commonService.ExecuteChain(params);
      if (res && res.success && res.content) {
        // 已有二维码保留原链接
        if (!initQCode) {
          setInitQCode(res.content.joinShortUrl);
          renderQrcode(res.content.joinShortUrl);
        }
        setIsCreatedQrcode(true);
        setQrCodeInDate([...dateRange]);
        return true;
      }
      message.error("生成二维码失败");
      return false;
    } catch (error) {
      console.error("生成二维码失败:", error);
      message.error("生成二维码失败");
      return false;
    } finally {
      setSpinning(false);
    }
  };

  /**
   * 复制链接
   */
  const copyUrl = () => {
    if (!initQCode) {
      message.warning("暂无链接可复制");
      return;
    }
    const text = initQCode;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(() => message.success("复制成功!"))
        .catch(() => fallbackCopy(text));
    } else {
      fallbackCopy(text);
    }
  };

  const fallbackCopy = (text) => {
    const oInput = document.createElement("input");
    oInput.value = text;
    document.body.appendChild(oInput);
    oInput.select();
    try {
      document.execCommand("Copy");
      message.success("复制成功!");
    } catch (err) {
      message.error("无法复制内容");
    }
    oInput.remove();
  };

  /**
   * 复制二维码图片
   * 用 html2canvas 将离屏卡片转为 PNG blob，写入剪贴板
   */
  const copyImage = () => {
    const dom = qrcodeCardRef.current;
    if (!dom) {
      message.warning("暂无二维码可复制");
      return;
    }
    html2canvas(dom, { useCORS: true, width: 300, height: 500 })
      .then((canvas) => {
        canvas.toBlob((blob) => {
          if (!blob) {
            message.error("生成图片失败");
            return;
          }
          const item = new ClipboardItem({ "image/png": blob });
          navigator.clipboard
            .write([item])
            .then(() => message.success("复制成功!"))
            .catch(() => message.error("浏览器不支持复制图片"));
        });
      })
      .catch(() => message.error("生成图片失败"));
  };

  /**
   * 下载二维码图片
   */
  const downloadCode = () => {
    const dom = qrcodeCardRef.current;
    if (!dom) {
      message.warning("暂无二维码可下载");
      return;
    }
    html2canvas(dom, { useCORS: true, width: 300, height: 500 })
      .then((canvas) => {
        const link = document.createElement("a");
        link.href = canvas.toDataURL("image/png");
        link.download = "SettleQrCode.png";
        link.click();
      })
      .catch(() => message.error("下载二维码失败"));
  };

  /**
   * 调整有效期 - 限制日期选择
   */
  const disabledDate = (current) => {
    if (!meetingQRCodeDateRange) return false;
    return (
      current &&
      (current.isBefore(moment(meetingQRCodeDateRange[0]), "day") ||
        current.isAfter(moment(meetingQRCodeDateRange[1]), "day"))
    );
  };

  /**
   * 调整有效期 popover 显隐控制
   */
  const handleTiemPopoverChange = (visible) => {
    setTimePopoverVisible(false);
    if (!visible) return;
    if (!meetingQRCodeDateRange) {
      message.warning("无会议日期信息，暂不能调整二维码有效期");
      return;
    }
    const today = moment();
    if (today.isBefore(moment(meetingQRCodeDateRange[0]), "day")) {
      message.warning("尚未进入会议有效期，暂不能调整二维码有效期");
      return;
    }
    if (today.isAfter(moment(meetingQRCodeDateRange[1]), "day")) {
      message.warning("会议已过期，无法调整二维码有效期");
      return;
    }
    if (qrCodeInDate) {
      setEchoData([moment(qrCodeInDate[0]), moment(qrCodeInDate[1])]);
    }
    setUnconfirmedTime(null);
    setTimePopoverVisible(true);
  };

  const cancelTime = () => {
    setEchoData(null);
    setUnconfirmedTime(null);
    setTimePopoverVisible(false);
  };

  const confirmTime = async () => {
    if (!unconfirmedTime) {
      setTimePopoverVisible(false);
      return;
    }
    const dateRange = unconfirmedTime;
    if (
      !dateRange[0] ||
      !dateRange[1] ||
      !moment(dateRange[0]).isValid() ||
      !moment(dateRange[1]).isValid() ||
      disabledDate(moment(dateRange[0])) ||
      disabledDate(moment(dateRange[1])) ||
      moment(dateRange[0]).isAfter(moment(dateRange[1]), "day")
    ) {
      message.error("请选择会议开始前一天至会议结束后一天范围内的有效期");
      return;
    }
    const ok = await createQRCodeContext(dateRange);
    if (ok) {
      setTimePopoverVisible(false);
      setUnconfirmedTime(null);
    }
  };

  /**
   * 批量编辑手机号回调
   */
  const changeQRCodeData = (phones) => {
    setPhoneList(phones);
    createQRCodeContext(undefined, phones);
  };

  const handleCancel = () => {
    if (onClose) onClose();
  };

  // 重置状态（弹窗关闭时）
  useEffect(() => {
    if (!visible) {
      setIsCreatedQrcode(false);
      setInitQCode("");
      setQrCodeInDate(null);
      setPhoneList([]);
      setQrData(defaultQrData);
      setTimePopoverVisible(false);
      setEchoData(null);
      setUnconfirmedTime(null);
      setPhoneModalViable(false);
      setLogVisible(false);
    }
  }, [visible]);

  return (
    <>
      <Modal
        title={qrData.title || "酒店结算二维码"}
        visible={visible}
        width={720}
        destroyOnClose
        maskClosable={false}
        footer={null}
        onCancel={handleCancel}
      >
        <Spin spinning={spinning}>
          {/* 未生成 → 显示执行人编辑表格 */}
          {!isCreatedQrcode && (
            <EditPhoneTable
              qrData={qrData}
              isCreatedQrcode={isCreatedQrcode}
              record={record}
              onCreateQRCode={createQRCode}
              onChangeQRCodeData={changeQRCodeData}
              onShowLogs={() => setLogVisible(true)}
            />
          )}

          {/* 已生成 → 显示二维码 + 分享按钮 */}
          {isCreatedQrcode && (
            <Row gutter={8} className="settle-qrcode-modal-content">
              <Col span={4}>
                <div className="code_box">
                  <div className="code_logo">
                    <QrcodeOutlined
                      style={{ fontSize: "20px", color: "#3875fe" }}
                    />
                  </div>
                  <canvas id="settleQrCode" ref={qrCodeRef} />
                </div>
              </Col>
              <Col span={13}>
                <div className="share-btns">
                  <Popover
                    content={
                      <div className="urlBox">
                        <div className="urlNotice">
                          请使用手机浏览器打开页面
                        </div>
                        <div className="urlContent">
                          链接：{initQCode}
                          <br />
                          {miceInfo?.name}
                          <br />
                          二维码有效期：
                          {qrCodeInDate ? qrCodeInDate.join("~") : ""}
                        </div>
                      </div>
                    }
                  >
                    <div className="share-btn" onClick={copyUrl}>
                      <div className="share-btn-icon icon_fujian">
                        <CopyOutlined
                          style={{ fontSize: "30px", color: "#fff" }}
                        />
                      </div>
                      复制链接
                    </div>
                  </Popover>
                  <Popover
                    overlayClassName="poperLayTable"
                    placement="top"
                    content={
                      <QrcodeCard
                        ref={qrcodeCardRef}
                        qrData={qrData}
                        initQCode={initQCode}
                        qrCodeInDate={qrCodeInDate}
                        miceInfo={miceInfo}
                      />
                    }
                  >
                    <div className="share-btn" onClick={copyImage}>
                      <div className="share-btn-icon icon_erweima">
                        <QrcodeOutlined
                          style={{ fontSize: "30px", color: "#fff" }}
                        />
                      </div>
                      复制二维码
                    </div>
                  </Popover>
                  <Popover
                    content={
                      <QrcodeCard
                        qrData={qrData}
                        initQCode={initQCode}
                        qrCodeInDate={qrCodeInDate}
                        miceInfo={miceInfo}
                      />
                    }
                  >
                    <div className="share-btn" onClick={downloadCode}>
                      <div className="share-btn-icon icon_download">
                        <DownloadOutlined
                          style={{ fontSize: "30px", color: "#fff" }}
                        />
                      </div>
                      下载到本地
                    </div>
                  </Popover>
                </div>
              </Col>
              <Col span={7} className="settle-qrcode-modal-content-right">
                <Button
                  className="right-btn"
                  block
                  type="primary"
                  onClick={() => setPhoneModalViable(true)}
                >
                  批量编辑手机号
                </Button>
                <Popover
                  placement="right"
                  trigger="click"
                  visible={timePopoverVisible}
                  onVisibleChange={handleTiemPopoverChange}
                  content={
                    <div className="popover_time">
                      <RangePicker
                        value={echoData}
                        placeholder={["开始时间", "结束时间"]}
                        onChange={(value, dateString) => {
                          setEchoData(value);
                          setUnconfirmedTime(dateString);
                        }}
                        disabledDate={disabledDate}
                      />
                      <div className="time_btn">
                        <Button className="time_btn_item" onClick={cancelTime}>
                          取消
                        </Button>
                        <Button
                          className="time_btn_item"
                          type="primary"
                          onClick={confirmTime}
                        >
                          确认
                        </Button>
                      </div>
                    </div>
                  }
                >
                  <Button className="right-btn" type="primary" block>
                    调整二维码有效期
                  </Button>
                </Popover>
              </Col>
              <Col span={24}>
                <div className="settle-qrcode-modal-content-bottom">
                  <div className="modal-content-bottom-title">
                    {miceInfo?.name}
                  </div>
                  <div>
                    二维码有效期：
                    {qrCodeInDate ? qrCodeInDate.join("~") : ""}
                  </div>
                </div>
              </Col>
            </Row>
          )}
        </Spin>
      </Modal>

      {/* 批量编辑手机号弹层 */}
      <PhoneModal
        phoneModalViable={phoneModalViable}
        isCreatedQrcode={isCreatedQrcode}
        qrData={qrData}
        record={record}
        onHandlePhoneModal={() => setPhoneModalViable(false)}
        onChangeQRCodeData={changeQRCodeData}
      />

      {/* 批量导入日志弹层 */}
      <BatchPhoneLogsModal
        visible={logVisible}
        qrData={qrData}
        record={record}
        onClose={() => setLogVisible(false)}
      />
    </>
  );
};

export default SettleQrcodeModal;
